import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { DialogTitle } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { RepliconDialog } from '~/modules/common/components';
import { useGetScheduleHoursForUserAndDateRange } from '../../common/hooks/useGetScheduleHoursForUserAndDateRange';
import AllocateResourceDialogContent from './AllocateResourceDialogContent';
import AllocateResourceDialogFooter from './AllocateResourceDialogFooter';
import useAllocateResourcePopupHandler from './hooks/useAllocateResourcePopupHandler';
import { useAllocateResourceDialogSettings } from './hooks/useAllocateResourceDialogSettings';
import AllocateResourceDialogLoadingState from './AllocateResourceDialogLoadingState';

const useDilaogTitleStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2, 2, 2, 2)
  }
}));

const AllocateResourceDialogTitleId = 'allocate-resources-dialog-title';

const AllocateResourceDialog = ({
  defaultUser,
  fetchProjectDetails,
  open,
  onClose,
  handleAddResourceAllocation,
  scale,
  showAddUser = false,
  showAddProject = false,
  isSaving = false,
  setExistingResourceAllocation,
  existingResourceAllocation,
  defaultDates,
  project
}) => {
  const dialogTitleClasses = useDilaogTitleStyles();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const checkIsDirty = useCallback(() => isFormDirty, [isFormDirty]);

  const {
    isLoading: isKeyValueSettingsLoading,
    dialogSettings
  } = useAllocateResourceDialogSettings();

  const {
    scheduledHours: totalScheduledHours,
    scheduleRules,
    loading: isLoadingScheduleRules
  } = useGetScheduleHoursForUserAndDateRange({
    userUri: defaultUser?.uri,
    dateRange: defaultDates,
    skip: !defaultUser
  });

  const { initialValues, validationSchema } = useAllocateResourcePopupHandler({
    scale,
    defaultDates,
    user: defaultUser,
    projectRole: null,
    totalScheduledHours,
    scheduleRules,
    project,
    savedShowAdvancedSettings: dialogSettings?.showAdvancedSettings,
    savedSelectedAllocationPeriod: dialogSettings?.selectedAllocationPeriod,
    savedDisplayUnit: dialogSettings?.displayUnit
  });

  return (
    <RepliconDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      data-qe-id="AllocateResourcesDialog"
      aria-labelledby={AllocateResourceDialogTitleId}
      checkIsDirty={checkIsDirty}
      enableUnsavedChangesWarning
      disableBackdropClick
      trapFocusWithDatePicker
    >
      <DialogTitle
        classes={dialogTitleClasses}
        id={AllocateResourceDialogTitleId}
      >
        <FormattedMessage id="allocateResourcesContent.allocateResource" />
      </DialogTitle>
      {isKeyValueSettingsLoading || isLoadingScheduleRules ? (
        <AllocateResourceDialogLoadingState
          open={open}
          onClose={onClose}
          showAddProject={showAddProject}
          showAddUser={showAddUser}
        />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleAddResourceAllocation}
          validationSchema={validationSchema}
        >
          <>
            <AllocateResourceDialogContent
              defaultUser={defaultUser}
              fetchProjectDetails={fetchProjectDetails}
              totalScheduledHours={totalScheduledHours}
              scale={scale}
              setExistingResourceAllocation={setExistingResourceAllocation}
              existingResourceAllocation={existingResourceAllocation}
              showAddUser={showAddUser}
              showAddProject={showAddProject}
              setIsFormDirty={setIsFormDirty}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
            <AllocateResourceDialogFooter
              onClose={onClose}
              isSaving={isSaving}
              isLoading={isLoading}
            />
          </>
        </Formik>
      )}
    </RepliconDialog>
  );
};

AllocateResourceDialog.propTypes = {
  defaultUser: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleAddResourceAllocation: PropTypes.func,
  scale: PropTypes.string,
  showAddUser: PropTypes.bool,
  showAddProject: PropTypes.bool,
  isSaving: PropTypes.bool,
  setExistingResourceAllocation: PropTypes.func,
  existingResourceAllocation: PropTypes.object,
  fetchProjectDetails: PropTypes.func,
  defaultDates: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  project: PropTypes.object
};

export default AllocateResourceDialog;
